// Libs
import classNames from 'classnames/bind';
import {
  Button,
  MenuItem,
  Pagination,
  PaginationProps,
  PaginationRenderItemParams,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  BASE_PAGINATION_ROW_PER_PAGE_OPTIONS,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './BasePagination.module.scss';
import { icons } from '~/assets';

type Props = PaginationProps & {
  currentRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  onRowsPerPageChange?: (rowsPerPage: number) => void;
};

const cx = classNames.bind(styles);

const BasePagination = (props: Props) => {
  //#region Destructuring Props
  const {
    count,
    page,
    onChange,
    currentRowsPerPage,
    rowsPerPageOptions = BASE_PAGINATION_ROW_PER_PAGE_OPTIONS,
    onRowsPerPageChange,
    ...restProps
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base', 'common/main']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [currentPage, setCurrentPage] = useState<number>(BASE_PAGINATION_DEFAULT_CURRENT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    BASE_PAGINATION_ROW_PER_PAGE_OPTIONS[DEFAULT_NUMBER_ZERO]
  );
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setCurrentPage(page ? page : BASE_PAGINATION_DEFAULT_CURRENT_PAGE);
  }, [page]);

  useEffect(() => {
    setRowsPerPage(
      currentRowsPerPage ? currentRowsPerPage : BASE_PAGINATION_ROW_PER_PAGE_OPTIONS[DEFAULT_NUMBER_ZERO]
    );
  }, [currentRowsPerPage]);
  //#endregion Implement Hook

  //#region Handle Function
  const handlePaginationChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    onChange && onChange(event, page);
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;

    if (newValue) {
      setRowsPerPage(Number(newValue));
      onRowsPerPageChange && onRowsPerPageChange(Number(newValue));
    }
  };
  //#endregion Handle Function

  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
      gap={'12px'}
      component={'div'}
      id='basePaginationComponent'
      className={cx('container')}
    >
      <Stack component={'div'} direction={'row'} alignItems={'center'} gap={'9px'}>
        <Typography color='secondary' component={'span'} className={cx('rowsPerPageContent')}>
          {t('pagination_row_per_page_show')}
        </Typography>

        <Select
          value={String(rowsPerPage) || EMPTY_STRING}
          displayEmpty
          variant='outlined'
          IconComponent={() => (
            <img
              src={icons.commonIconArrowDown}
              alt={t('alt_img', { ns: 'common/main' })}
              className={cx('selectIcon')}
            />
          )}
          autoWidth
          className={cx('rowsPerPage')}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            className: cx('menu'),
            PaperProps: {
              className: cx('paper'),
            },
            MenuListProps: {
              className: cx('menuList'),
            },
          }}
          sx={{
            '.MuiSelect-select': {
              height: '100% !important',
              paddingLeft: '10px',
              display: 'flex',
              alignItems: 'center',
            },
          }}
          onChange={handleRowsPerPageChange}
        >
          {rowsPerPageOptions?.map((option) => (
            <MenuItem key={option} value={option} className={cx('menuItem')}>
              {option}
            </MenuItem>
          ))}
        </Select>

        <Typography color='secondary' component={'span'} className={cx('rowsPerPageContent')}>
          {t('pagination_row_per_page_entries')}
        </Typography>
      </Stack>

      <Pagination
        color='primary'
        count={count}
        shape='rounded'
        page={currentPage}
        onChange={(event: React.ChangeEvent<unknown>, page: number) => handlePaginationChange(event, page)}
        renderItem={(params: PaginationRenderItemParams) => {
          const { type, disabled, page, selected, onClick } = params;

          if (type === 'previous') {
            return (
              <Button
                variant='text'
                disabled={disabled}
                startIcon={
                  <img
                    className={cx('prevIcon')}
                    src={disabled ? icons.paginationIconPreviousDisabled : icons.paginationIconPreviousActive}
                    alt={t('alt_img', { ns: 'common/main' })}
                  />
                }
                className={cx('btnPrev')}
                onClick={onClick}
              >
                <Typography component={'span'} className={cx('prevContent')}>
                  {t('pagination_previous_title')}
                </Typography>
              </Button>
            );
          }

          if (type === 'next') {
            return (
              <Button
                variant='text'
                disabled={disabled}
                endIcon={
                  <img
                    className={cx('nextIcon')}
                    src={disabled ? icons.paginationIconNextDisabled : icons.paginationIconNextActive}
                    alt={t('alt_img')}
                  />
                }
                className={cx('btnNext')}
                onClick={onClick}
              >
                <Typography component={'span'} className={cx('nextContent')}>
                  {t('pagination_next_title')}
                </Typography>
              </Button>
            );
          }

          if (type === 'page') {
            return (
              <Button variant='text' className={cx('page', { selected: selected })} onClick={onClick}>
                <Typography component={'span'} className={cx('pageNumber')}>
                  {page}
                </Typography>
              </Button>
            );
          }

          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
            return (
              <Button variant='text' className={cx('page', 'ellipsis')}>
                ...
              </Button>
            );
          }

          return null;
        }}
        {...restProps}
      />
    </Stack>
  );
};

export default BasePagination;
